import React from 'react'
import { Typography, TextField, Box, Button, Autocomplete } from '@mui/material'
import { IStockCardProducts } from 'interfaces/IStockCard'
import { IProduct } from 'interfaces/IProduct'
import { VALUE_IN } from 'enums/stockCard'
import LoadProductAutocomplete from 'shared/autocomplete/loadProductAutocomplete'
import { cleanCorruptedString } from 'components/production/stockCardUtils'
import {
  formatStockQuantity,
  formatStockQuantityAuto,
} from 'components/inventory/helpers'
import { formatDecimalAuto } from 'shared/numberFormatter'
import { clampDecimalPlaces } from 'utils/clampDecimalPlaces'
import { Department } from 'enums/department'

interface Props {
  stockCardProduct: IStockCardProducts
  inOutDirection: string
  products?: IProduct[]
  onChange: (value: any, key: string) => void
  disabled?: boolean
  isConfirmed?: boolean
  isEditing?: boolean
  onRemove?: () => void
  hideSummary?: boolean
  brandId?: string
  packagingName?: string
  pictureUrl?: string | null
  labelId?: string
  notes?: string
  createdAt?: string
  currentStock?: number | null
  outgoingDestination?: string
  isSuperuser?: boolean
  createdAtLabel?: string
  index?: number
  packagingId?: string
  confirmedQtyLabel?: string
  fillingLabel?: string
  getMatchingBatch?: (item: { batchNumber?: string | null }) => any
  isBatchLoading?: boolean
  autoFormatQuantity?: boolean
  department?: Department
  batchOptions?: Array<{
    batchNumber: string
    productStock: number
    currentQuantity?: number
    expiryDate?: string | null
    productionDate?: string | null
  }>
}

const FieldRow = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <Box
    display="flex"
    borderBottom="1px solid #eee"
    py={1.5}
    alignItems="center"
  >
    <Typography
      style={{
        width: '30%',
        fontWeight: 500,
        fontSize: '0.95rem',
        color: '#666',
      }}
    >
      {label}
    </Typography>
    <Box flexGrow={1}>{children}</Box>
  </Box>
)

export default function StockCardPremixItem(props: Props) {
  const {
    stockCardProduct,
    inOutDirection,
    products,
    onChange,
    disabled,
    isConfirmed,
    isEditing,
    onRemove,
    hideSummary,
    brandId,
    packagingName,
    pictureUrl,
    labelId,
    notes,
    createdAt,
    createdAtLabel,
    currentStock,
    packagingId,
    confirmedQtyLabel: confirmedQtyLabelProp,
    fillingLabel: fillingLabelProp,
    isBatchLoading,
    autoFormatQuantity,
    department,
    batchOptions,
  } = props

  const formatQty = autoFormatQuantity
    ? formatStockQuantityAuto
    : formatStockQuantity

  const isEditable = isEditing || !isConfirmed

  const currentProduct = products?.find(
    (p) =>
      p.productId?.toString().toLowerCase() ===
      stockCardProduct.productId?.toString().toLowerCase()
  )

  const qtyLabel = inOutDirection === VALUE_IN ? 'Qty Diminta' : 'Qty Diminta'
  const confirmedQtyLabel =
    confirmedQtyLabelProp ||
    (inOutDirection === VALUE_IN ? 'Jumlah Diterima' : 'Jumlah Dikeluarkan')
  const fillingLabel = fillingLabelProp || 'Filling'
  const currentStockLabel = 'Stok Sekarang'
  const canChooseBatch =
    !isConfirmed && inOutDirection !== VALUE_IN && !!batchOptions?.length
  const selectedBatchOption =
    batchOptions?.find(
      (batch) =>
        String(batch.batchNumber || '').toLowerCase() ===
        String(stockCardProduct.batchNumber || '').toLowerCase()
    ) ?? null

  const itemPictureUrl = (() => {
    // 1. Check stockCardProduct.pictureUrl (if valid URL)
    const pUrl = stockCardProduct.pictureUrl
    if (
      pUrl &&
      typeof pUrl === 'string' &&
      (pUrl.startsWith('http') || pUrl.startsWith('blob'))
    ) {
      return pUrl
    }

    // 2. Check props.pictureUrl (parent URL) if packaging matches
    if (
      pictureUrl &&
      packagingId &&
      stockCardProduct.packagingId === packagingId
    ) {
      return pictureUrl
    }

    // 3. Fallback to product.pictureUrl (if valid URL)
    const prodUrl = stockCardProduct.product?.pictureUrl
    if (
      prodUrl &&
      typeof prodUrl === 'string' &&
      (prodUrl.startsWith('http') || prodUrl.startsWith('blob'))
    ) {
      return prodUrl
    }

    return null
  })()

  const fillingDisplay = (() => {
    // @ts-ignore
    if (stockCardProduct.filling) return stockCardProduct.filling // Backward compatibility if filling exists

    const amount =
      stockCardProduct.fillingAmount ??
      stockCardProduct.product?.fillingAmount ??
      stockCardProduct.product?.weight
    const unit =
      stockCardProduct.fillingUnit ??
      stockCardProduct.product?.fillingUnit ??
      stockCardProduct.product?.unit ??
      ''

    if (amount === undefined || amount === null || amount === '') return '-'
    return `${formatDecimalAuto(amount)}${unit}`
  })()

  const currentStockValue = formatQty(
    selectedBatchOption?.productStock ??
      currentProduct?.stock ??
      currentStock ??
      stockCardProduct.latestStock ??
      0
  )

  return (
    <Box width="100%" py={1}>
      {!hideSummary && (
        <Box width="100%">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              Item Produk
            </Typography>
            {onRemove && (
              <Button
                color="secondary"
                onClick={onRemove}
                size="small"
                variant="outlined"
                style={{ textTransform: 'none' }}
              >
                Hapus
              </Button>
            )}
          </Box>

          {isEditable ? (
            <Box>
              <FieldRow label="Nama Produk">
                {isEditing ? (
                  <LoadProductAutocomplete
                    type={
                      stockCardProduct.type ||
                      (inOutDirection === VALUE_IN
                        ? 'SETENGAH_JADI'
                        : 'BARANG_JADI')
                    }
                    name={stockCardProduct.productId?.toString()}
                    onChange={(e: IProduct) => {
                      onChange(e, 'product')
                    }}
                    department={department}
                  />
                ) : (
                  <Typography style={{ fontWeight: 600 }}>
                    {stockCardProduct.productName}
                  </Typography>
                )}
              </FieldRow>

              <FieldRow label="ID Produk">
                <Typography style={{ fontWeight: 600 }}>
                  {stockCardProduct.productId || stockCardProduct.product?.productId || '-'}
                </Typography>
              </FieldRow>

              <FieldRow label="No. Bets">
                {canChooseBatch ? (
                  <Autocomplete
                    options={batchOptions || []}
                    value={selectedBatchOption}
                    getOptionLabel={(option) =>
                      `${option.batchNumber} (Stok: ${formatQty(
                        option.productStock ?? 0
                      )})`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.batchNumber === value.batchNumber
                    }
                    onChange={(_, option) =>
                      onChange(option?.batchNumber || '', 'batchNumber')
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    )}
                    disabled={disabled}
                  />
                ) : isEditing && inOutDirection === VALUE_IN ? (
                  <TextField
                    value={stockCardProduct.batchNumber}
                    onChange={(e) => onChange(e.target.value, 'batchNumber')}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                ) : (
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontStyle: !stockCardProduct.batchNumber
                        ? 'italic'
                        : 'normal',
                      color: !stockCardProduct.batchNumber ? '#888' : 'inherit',
                    }}
                  >
                    {cleanCorruptedString(stockCardProduct.batchNumber) ||
                      'Tidak ada bets'}
                  </Typography>
                )}
              </FieldRow>

              <FieldRow label="Kode Ruahan">
                {isEditing ? (
                  <TextField
                    value={stockCardProduct.premixId}
                    onChange={(e) => onChange(e.target.value, 'premixId')}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                ) : (
                  <Typography style={{ fontWeight: 600 }}>
                    {stockCardProduct.premixId || '-'}
                  </Typography>
                )}
              </FieldRow>

              <FieldRow label={fillingLabel}>
                {isEditing || (fillingLabelProp && !disabled) ? (
                  <Box display="flex" alignItems="center">
                    <TextField
                      value={
                        stockCardProduct.fillingAmount ??
                        (isEditing ? stockCardProduct.filling : undefined) ??
                        ''
                      }
                      onChange={(e) =>
                        onChange(e.target.value, 'fillingAmount')
                      }
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="Amount"
                      disabled={disabled}
                    />
                    <Box ml={1} minWidth="80px">
                      <TextField
                        value={stockCardProduct.fillingUnit ?? ''}
                        onChange={(e) =>
                          onChange(e.target.value, 'fillingUnit')
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Unit"
                        disabled={disabled}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Typography style={{ fontWeight: 600 }}>
                    {fillingDisplay}
                  </Typography>
                )}
              </FieldRow>

              <FieldRow label={qtyLabel}>
                {isEditing ? (
                  <TextField
                    value={stockCardProduct.quantity}
                    onChange={(e) =>
                      onChange(clampDecimalPlaces(e.target.value), 'quantity')
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                ) : (
                  <Typography style={{ fontWeight: 600 }}>
                    {formatDecimalAuto(stockCardProduct.quantity)}
                  </Typography>
                )}
              </FieldRow>

              <FieldRow label={confirmedQtyLabel}>
                <TextField
                  required
                  autoComplete="off"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={stockCardProduct.confirmedQuantity ?? ''}
                  onChange={(e) =>
                    onChange(
                      clampDecimalPlaces(e.target.value),
                      'confirmedQuantity'
                    )
                  }
                  disabled={disabled}
                />
              </FieldRow>

              <FieldRow label={currentStockLabel}>
                {(isBatchLoading ||
                  (!products && currentStock === undefined)) &&
                stockCardProduct.batchNumber ? (
                  <Typography
                    color="textSecondary"
                    style={{ fontStyle: 'italic' }}
                  >
                    Loading...
                  </Typography>
                ) : (
                  <Typography style={{ fontWeight: 600 }}>
                    {currentStockValue}
                  </Typography>
                )}
              </FieldRow>

              {brandId && (
                <FieldRow label="Merek">
                  <Typography style={{ fontWeight: 600 }}>{brandId}</Typography>
                </FieldRow>
              )}
              {(packagingId || stockCardProduct.packagingId) && (
                <FieldRow label="Kode Kemasan">
                  <Typography style={{ fontWeight: 600 }}>
                    {stockCardProduct.packagingId || packagingId}
                  </Typography>
                </FieldRow>
              )}
              {(packagingName || stockCardProduct.packagingName) && (
                <FieldRow label="Nama Kemasan">
                  <Typography style={{ fontWeight: 600 }}>
                    {cleanCorruptedString(
                      stockCardProduct.packagingName || packagingName
                    )}
                  </Typography>
                </FieldRow>
              )}
              {itemPictureUrl && (
                <FieldRow label="Gambar Kemasan">
                  <img
                    src={itemPictureUrl}
                    alt="Kemasan"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'contain',
                    }}
                  />
                </FieldRow>
              )}
              {labelId && (
                <FieldRow label="Label ID">
                  <Typography style={{ fontWeight: 600 }}>{labelId}</Typography>
                </FieldRow>
              )}
              {notes && (
                <FieldRow label="Notes Produksi">
                  <Typography style={{ fontWeight: 600 }}>{notes}</Typography>
                </FieldRow>
              )}
              {createdAt && (
                <FieldRow label={createdAtLabel || 'Tanggal Labelling Selesai'}>
                  <Typography style={{ fontWeight: 600 }}>
                    {createdAt}
                  </Typography>
                </FieldRow>
              )}
            </Box>
          ) : (
            <Box>
              <FieldRow label="Nama Produk">
                <Typography style={{ fontWeight: 600 }}>
                  {stockCardProduct.productName || '-'}
                </Typography>
              </FieldRow>
              <FieldRow label="ID Produk">
                <Typography style={{ fontWeight: 600 }}>
                  {stockCardProduct.productId || stockCardProduct.product?.productId || '-'}
                </Typography>
              </FieldRow>
              <FieldRow label="No. Bets">
                <Typography
                  style={{
                    fontWeight: 600,
                    fontStyle: !stockCardProduct.batchNumber
                      ? 'italic'
                      : 'normal',
                    color: !stockCardProduct.batchNumber ? '#888' : 'inherit',
                  }}
                >
                  {cleanCorruptedString(stockCardProduct.batchNumber) ||
                    'Tidak ada bets'}
                </Typography>
              </FieldRow>
              <FieldRow label="Kode Ruahan">
                <Typography style={{ fontWeight: 600 }}>
                  {stockCardProduct.premixId || '-'}
                </Typography>
              </FieldRow>
              <FieldRow label={fillingLabel}>
                <Typography style={{ fontWeight: 600 }}>
                  {fillingDisplay}
                </Typography>
              </FieldRow>
              <FieldRow label={qtyLabel}>
                <Typography style={{ fontWeight: 600 }}>
                  {formatDecimalAuto(stockCardProduct.quantity ?? 0)}
                </Typography>
              </FieldRow>
              <FieldRow label={confirmedQtyLabel}>
                <Typography style={{ fontWeight: 600 }}>
                  {formatDecimalAuto(stockCardProduct.confirmedQuantity)}
                </Typography>
              </FieldRow>
              <FieldRow label={currentStockLabel}>
                {(isBatchLoading ||
                  (!products && currentStock === undefined)) &&
                stockCardProduct.batchNumber ? (
                  <Typography
                    color="textSecondary"
                    style={{ fontStyle: 'italic' }}
                  >
                    Loading...
                  </Typography>
                ) : (
                  <Typography style={{ fontWeight: 600 }}>
                    {currentStockValue}
                  </Typography>
                )}
              </FieldRow>

              {brandId && (
                <FieldRow label="Merek">
                  <Typography style={{ fontWeight: 600 }}>{brandId}</Typography>
                </FieldRow>
              )}
              {(packagingId || stockCardProduct.packagingId) && (
                <FieldRow label="Kode Kemasan">
                  <Typography style={{ fontWeight: 600 }}>
                    {stockCardProduct.packagingId || packagingId}
                  </Typography>
                </FieldRow>
              )}
              {(packagingName || stockCardProduct.packagingName) && (
                <FieldRow label="Nama Kemasan">
                  <Typography style={{ fontWeight: 600 }}>
                    {cleanCorruptedString(
                      stockCardProduct.packagingName || packagingName
                    )}
                  </Typography>
                </FieldRow>
              )}
              {itemPictureUrl && (
                <FieldRow label="Gambar Kemasan">
                  <img
                    src={itemPictureUrl}
                    alt="Kemasan"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'contain',
                    }}
                  />
                </FieldRow>
              )}
              {labelId && (
                <FieldRow label="Label ID">
                  <Typography style={{ fontWeight: 600 }}>{labelId}</Typography>
                </FieldRow>
              )}
              {notes && (
                <FieldRow label="Notes Produksi">
                  <Typography style={{ fontWeight: 600 }}>{notes}</Typography>
                </FieldRow>
              )}
              {createdAt && (
                <FieldRow label={createdAtLabel || 'Tanggal Labelling Selesai'}>
                  <Typography style={{ fontWeight: 600 }}>
                    {createdAt}
                  </Typography>
                </FieldRow>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
